import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { Router } from '@uhg-abyss/web/ui/Router';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '../../auth';
import { AuthInterceptedError } from '../../common/ErrorHandler';
import { SharedDialog } from '../../common/SharedDialog';
import {
  NO_HORIZONTAL_SCROLL_TABLE_STYLES,
  getResponsiveColumnSizes,
  useTableContainerWidth,
} from '../../common/dataTable/dataTableLayout';
import { TWO_STATE_SORTING_TABLE_CONFIG } from '../../common/dataTable/twoStateSorting';
import { downloadSurveyExcel } from '../Home/downloadSurveyExcel';
import {
  getAllQuestionType,
  getHistoricalPoliciesByPolicyIds,
  searchHistoricalPoliciesBySearchId,
  type PolicySummary,
} from './historicalDataApi';
import {
  filterTableRows,
  getPolicyTableRows,
  splitPolicyList,
  type HistoricalTableRow,
} from './tableRows';

const FALLBACK_VALUE = 'N/A';
const DEFAULT_STATUS = 'Open';
const QUICK_FILTER_MIN_LENGTH = 3;
const PAGE_SIZE_OPTIONS = [5, 10, 20];
const YEAR_OPTION_COUNT = 7;
const YEAR_PROMPT_OPTION = { label: 'All Years', value: '' };
const NO_RECORDS_MESSAGE =
  "The RFI database does not have record that this Employer Group/Policy submitted an RFI through this RFI portal in previous years. Please proceed to the Home screen and complete this year's RFI.";
const COLUMN_LAYOUT = {
  employerGroupName: { min: 160, max: 320, flex: 2.2 },
  policyNumber: { min: 110, max: 190, flex: 1 },
  surveyYear: { min: 70, max: 90, flex: 0.45 },
  fundingType: { min: 90, max: 120, flex: 0.65 },
  userName: { min: 120, max: 180, flex: 1.25 },
  surveyStartDate: { min: 120, max: 160, flex: 1 },
  surveyCompletedDate: { min: 120, max: 160, flex: 1 },
  surveyStatus: { min: 100, max: 135, flex: 0.8 },
  download: { min: 88, max: 108, flex: 0.2 },
} as const;

const STATUS_HELP_TEXT = (
  <React.Fragment>
    <div>
      <strong>Open:</strong> RFI is not started.
    </div>
    <div>
      <strong>In Process:</strong> RFI is started, but responses have not been
      submitted to UnitedHealthcare. RFIs with status In Process are considered
      incomplete and will not be included in the submission to CMS.
    </div>
    <div>
      <strong>Complete:</strong> RFI has been successfully submitted to
      UnitedHealthcare.
    </div>
    <div>Note: Responses can be modified until the data collection period ends.</div>
  </React.Fragment>
);

const COLUMN_HELP_TEXT = {
  userName: 'This is the name of the individual that submitted the RFI.',
  surveyStartDate: 'This is the date the RFI was started.',
  surveyCompletedDate:
    'This is the date the RFI was submitted to UnitedHealthcare.',
  download:
    "Click 'Download' to generate a form containing a prior year RFI response.",
  surveyStatus: STATUS_HELP_TEXT,
};

const Page = styled('div', {
  width: '100%',
  display: 'grid',
  gap: '1rem',
});

const Section = styled('section', {
  width: '100%',
});

const ToolbarPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '1.25rem',
  boxSizing: 'border-box',
});

const ToolbarPanelContent = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'end',
  flexWrap: 'wrap',
});

const FieldLabel = styled(Text, {
  display: 'block',
  color: '$web.semantic.color.text.content.primary',
  fontWeight: 600,
  lineHeight: 1.2,
});

const InputGroup = styled('div', {
  flex: '1 1 220px',
  minWidth: 0,
  display: 'grid',
  gap: '0.35rem',
});

const SearchInputGroup = styled(InputGroup, {
  flex: '2 1 320px',
  minWidth: '260px',
});

const YearSelectGroup = styled(InputGroup, {
  flex: '0 1 180px',
  minWidth: '160px',
});

const FieldControls = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.5rem',
  alignItems: 'center',
  minWidth: 0,
});

const TextInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  minHeight: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  fontSize: '0.95rem',
  padding: '0 0.75rem',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const DataTableWrapper = styled('div', {
  width: '100%',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '0',
  boxSizing: 'border-box',
  ...NO_HORIZONTAL_SCROLL_TABLE_STYLES,
  '& .abyss-data-table-slot-wrapper': {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  '& .abyss-data-table-pagination': {
    marginTop: 0,
    paddingTop: 0,
  },
});

const InlineLinkButton = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  textDecoration: 'underline',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '2px',
  },
});

const PolicyListOverflowTrigger = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  flexShrink: 0,
  color: 'inherit',
  textDecoration: 'none',
  font: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const HeaderWithHelp = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
});

const HelpButton = styled('button', {
  width: '1rem',
  height: '1rem',
  minWidth: '1rem',
  minHeight: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '9999px',
  border:
    '1px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
  backgroundColor:
    '$web.semantic.color.surface.interactive.standards.rest.default.primary',
  color: '$web.semantic.color.text.label.cta.primary-alt',
  fontSize: '0.7rem',
  fontWeight: 700,
  cursor: 'pointer',
});

const PolicyListCell = styled('div', {
  display: 'block',
  minWidth: 0,
  maxWidth: '100%',
  whiteSpace: 'normal',
});

const PolicyListCellText = styled('span', {
  display: 'inline',
  maxWidth: '100%',
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

function filterStringValues(values: unknown[]): string[] {
  return values.filter((value): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
  });
}

function extractPolicyIdsFromParsed(parsed: unknown): string[] | null {
  if (Array.isArray(parsed)) {
    return filterStringValues(parsed);
  }

  if (typeof parsed === 'object' && parsed && 'policyIds' in parsed) {
    const policyIds = (parsed as { policyIds?: unknown }).policyIds;
    if (Array.isArray(policyIds)) {
      return filterStringValues(policyIds);
    }
  }

  return null;
}

function fallbackParsePolicyIds(rawPolicyDetails: string): string[] {
  if (rawPolicyDetails.includes(',')) {
    return rawPolicyDetails
      .split(',')
      .map((value) => {
        return value.trim();
      })
      .filter(Boolean);
  }

  if (rawPolicyDetails.trim()) {
    return [rawPolicyDetails.trim()];
  }

  return [];
}

function readPolicyIdsFromSessionStorage(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const rawPolicyDetails = window.sessionStorage.getItem('policy_details');
  if (!rawPolicyDetails) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawPolicyDetails) as unknown;
    return extractPolicyIdsFromParsed(parsed) ?? [];
  } catch {
    return fallbackParsePolicyIds(rawPolicyDetails);
  }
}

function getDefaultHistoricalYear(now = new Date()) {
  const currentYear = now.getFullYear();
  const juneFirst = new Date(currentYear, 5, 1, 0, 0, 0, 0);

  return String(now > juneFirst ? currentYear : currentYear - 1);
}

function getHistoricalYearOptions(now = new Date()) {
  const defaultYear = Number(getDefaultHistoricalYear(now));

  return Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => {
    const year = String(defaultYear - index);
    return { label: year, value: year };
  });
}

function getColumnHeader(label: string, helpText?: React.ReactNode) {
  if (!helpText) {
    return label;
  }

  return (
    <HeaderWithHelp>
      <span>{label}</span>
      <Tooltip
        content={helpText}
        position="top"
        variant="white"
        delayDuration={200}
      >
        <HelpButton aria-label={`${label} information`} type="button">
          ?
        </HelpButton>
      </Tooltip>
    </HeaderWithHelp>
  );
}

function renderPolicyListCell(policyList: string) {
  const values = splitPolicyList(policyList, FALLBACK_VALUE);
  if (values.length <= 2) {
    return (
      <PolicyListCell>
        <PolicyListCellText>{policyList}</PolicyListCellText>
      </PolicyListCell>
    );
  }

  const visible = values.slice(0, 2).join(', ');
  const fullPolicyList = values.join(';');

  return (
    <PolicyListCell>
      <PolicyListCellText>{visible}</PolicyListCellText>
      <Tooltip
        content={fullPolicyList}
        position="top"
        variant="white"
        delayDuration={200}
      >
        <PolicyListOverflowTrigger
          type="button"
          aria-label={`Show all policy values: ${fullPolicyList}`}
        >
          ...
        </PolicyListOverflowTrigger>
      </Tooltip>
    </PolicyListCell>
  );
}

function getHistoricalColumns(args: {
  containerWidth: number;
  onRowClick: (row: HistoricalTableRow) => void;
  onDownloadClick: (row: HistoricalTableRow) => void;
}) {
  const columnSizes = getResponsiveColumnSizes(args.containerWidth, COLUMN_LAYOUT, 'employerGroupName');

  return [
    createColumn<HistoricalTableRow>({
      id: 'employerGroupName',
      accessorKey: 'employerGroupName',
      header: 'Employer Group Name',
      minSize: COLUMN_LAYOUT.employerGroupName.min,
      size: columnSizes.employerGroupName,
      maxSize: Math.max(
        COLUMN_LAYOUT.employerGroupName.max,
        columnSizes.employerGroupName
      ),
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        return (
          <InlineLinkButton
            type="button"
            onClick={() => {
              args.onRowClick(context.row.original);
            }}
          >
            {context.row.original.employerGroupName}
          </InlineLinkButton>
        );
      },
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'policyNumber',
      accessorKey: 'policyNumber',
      header: 'Policy List',
      minSize: COLUMN_LAYOUT.policyNumber.min,
      size: columnSizes.policyNumber,
      maxSize: Math.max(COLUMN_LAYOUT.policyNumber.max, columnSizes.policyNumber),
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        return renderPolicyListCell(context.row.original.policyNumber);
      },
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyYear',
      accessorKey: 'surveyYear',
      header: 'Ref Year',
      minSize: COLUMN_LAYOUT.surveyYear.min,
      size: columnSizes.surveyYear,
      maxSize: Math.max(COLUMN_LAYOUT.surveyYear.max, columnSizes.surveyYear),
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'fundingType',
      accessorKey: 'fundingType',
      header: 'Funding Type',
      minSize: COLUMN_LAYOUT.fundingType.min,
      size: columnSizes.fundingType,
      maxSize: Math.max(COLUMN_LAYOUT.fundingType.max, columnSizes.fundingType),
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'userName',
      accessorKey: 'userName',
      header: () => {
        return getColumnHeader('Submitter Name', COLUMN_HELP_TEXT.userName);
      },
      minSize: COLUMN_LAYOUT.userName.min,
      size: columnSizes.userName,
      maxSize: Math.max(COLUMN_LAYOUT.userName.max, columnSizes.userName),
      enableSorting: true,
      meta: { headerLabel: 'Submitter Name' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyStartDate',
      accessorKey: 'surveyStartDate',
      header: () => {
        return getColumnHeader(
          'Date Started',
          COLUMN_HELP_TEXT.surveyStartDate
        );
      },
      minSize: COLUMN_LAYOUT.surveyStartDate.min,
      size: columnSizes.surveyStartDate,
      maxSize: Math.max(
        COLUMN_LAYOUT.surveyStartDate.max,
        columnSizes.surveyStartDate
      ),
      enableSorting: true,
      meta: { headerLabel: 'Date Started' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyCompletedDate',
      accessorKey: 'surveyCompletedDate',
      header: () => {
        return getColumnHeader(
          'Date Submitted',
          COLUMN_HELP_TEXT.surveyCompletedDate
        );
      },
      minSize: COLUMN_LAYOUT.surveyCompletedDate.min,
      size: columnSizes.surveyCompletedDate,
      maxSize: Math.max(
        COLUMN_LAYOUT.surveyCompletedDate.max,
        columnSizes.surveyCompletedDate
      ),
      enableSorting: true,
      meta: { headerLabel: 'Date Submitted' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyStatus',
      accessorKey: 'surveyStatus',
      header: () => {
        return getColumnHeader('RFI Status', COLUMN_HELP_TEXT.surveyStatus);
      },
      minSize: COLUMN_LAYOUT.surveyStatus.min,
      size: columnSizes.surveyStatus,
      maxSize: Math.max(COLUMN_LAYOUT.surveyStatus.max, columnSizes.surveyStatus),
      enableSorting: true,
      meta: { headerLabel: 'RFI Status' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'download',
      accessorKey: 'download',
      header: () => {
        return getColumnHeader('Download RFI', COLUMN_HELP_TEXT.download);
      },
      minSize: COLUMN_LAYOUT.download.min,
      size: columnSizes.download,
      maxSize: Math.max(COLUMN_LAYOUT.download.max, columnSizes.download),
      enableSorting: false,
      meta: { headerLabel: 'Download RFI' },
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        const row = context.row.original;
        if (row.download !== 'Download') {
          return null;
        }

        return (
          <InlineLinkButton
            type="button"
            aria-label={`Download RFI for ${row.employerGroupName}`}
            onClick={() => {
              args.onDownloadClick(row);
            }}
          >
            Download
          </InlineLinkButton>
        );
      },
    }),
  ];
}

function computeShouldShowNoRecords(params: {
  isInternalUser: boolean;
  isLoading: boolean;
  errorMessage: string | null;
  hasSearched: boolean;
  rowCount: number;
}) {
  if (params.isLoading || params.errorMessage) {
    return false;
  }

  return params.isInternalUser
    ? params.hasSearched && params.rowCount === 0
    : params.rowCount === 0;
}

type YearOption = { label: string; value: string };

function InternalToolbar(props: {
  searchKey: string;
  onSearchKeyChange: (value: string) => void;
  onSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchClick: () => void;
  canRunSearch: boolean;
  quickFilterInput: string;
  onQuickFilterChange: (value: string) => void;
  onQuickFilterClear: () => void;
  canClearQuickFilter: boolean;
  selectedYear: string;
  onYearChange: (value: string) => void;
  yearOptions: YearOption[];
  hasResults: boolean;
  showRecordCount: boolean;
  recordCount: number;
}) {
  return (
    <Section>
      <ToolbarPanel>
        <ToolbarPanelContent>
          <Heading level={3} size="md" textAlign="left">
            Prior Years RFI Search
          </Heading>
          <ToolbarRow>
            <SearchInputGroup>
              <FieldLabel>Search historical records</FieldLabel>
              <FieldControls>
                <TextInput
                  aria-label="Search historical RFIs"
                  type="text"
                  value={props.searchKey}
                  placeholder="Search for policies by Policy number or Employer Group Name or EIN"
                  onChange={(event) => {
                    props.onSearchKeyChange(event.target.value);
                  }}
                  onKeyDown={props.onSearchKeyDown}
                />
                <Button
                  type="button"
                  variant={props.canRunSearch ? 'filled' : 'outline'}
                  size="sm"
                  disabled={!props.canRunSearch}
                  onClick={props.onSearchClick}
                >
                  Search
                </Button>
              </FieldControls>
            </SearchInputGroup>
            {props.hasResults ? (
              <InputGroup>
                <FieldLabel>Quick filter</FieldLabel>
                <FieldControls>
                  <TextInput
                    aria-label="Filter historical results"
                    type="text"
                    value={props.quickFilterInput}
                    placeholder="Filter by Employer Group Name in the List"
                    onChange={(event) => {
                      props.onQuickFilterChange(event.target.value);
                    }}
                  />
                  <Button
                    type="button"
                    variant={props.canClearQuickFilter ? 'filled' : 'outline'}
                    size="sm"
                    disabled={!props.canClearQuickFilter}
                    onClick={props.onQuickFilterClear}
                  >
                    Clear
                  </Button>
                </FieldControls>
              </InputGroup>
            ) : null}
            <YearSelectGroup>
              <SelectInput
                label="RFI Year"
                value={props.selectedYear}
                onChange={(value) => {
                  props.onYearChange(String(value ?? ''));
                }}
                options={props.yearOptions}
                width="100%"
              />
            </YearSelectGroup>
          </ToolbarRow>

          {props.showRecordCount ? (
            <Text>{props.recordCount} records found</Text>
          ) : null}
        </ToolbarPanelContent>
      </ToolbarPanel>
    </Section>
  );
}

function ExternalToolbar(props: {
  quickFilterInput: string;
  onQuickFilterChange: (value: string) => void;
  onQuickFilterClear: () => void;
  canClearQuickFilter: boolean;
  selectedYear: string;
  onYearChange: (value: string) => void;
  yearOptions: YearOption[];
  hasResults: boolean;
  showRecordCount: boolean;
  recordCount: number;
}) {
  return (
    <Section>
      <ToolbarPanel>
        <ToolbarPanelContent>
          <Heading level={3} size="md" textAlign="left">
            Prior Years RFI Filters
          </Heading>
          <ToolbarRow>
            {props.hasResults ? (
              <InputGroup>
                <FieldLabel>Quick filter</FieldLabel>
                <FieldControls>
                  <TextInput
                    aria-label="Filter historical results"
                    type="text"
                    value={props.quickFilterInput}
                    placeholder="Filter by Employer Group Name in the List"
                    onChange={(event) => {
                      props.onQuickFilterChange(event.target.value);
                    }}
                  />
                  <Button
                    type="button"
                    variant={props.canClearQuickFilter ? 'filled' : 'outline'}
                    size="sm"
                    disabled={!props.canClearQuickFilter}
                    onClick={props.onQuickFilterClear}
                  >
                    Clear
                  </Button>
                </FieldControls>
              </InputGroup>
            ) : null}
            <YearSelectGroup>
              <SelectInput
                label="RFI Year"
                value={props.selectedYear}
                onChange={(value) => {
                  props.onYearChange(String(value ?? ''));
                }}
                options={props.yearOptions}
                width="100%"
              />
            </YearSelectGroup>
          </ToolbarRow>

          {props.showRecordCount ? (
            <Text>{props.recordCount} records found</Text>
          ) : null}
        </ToolbarPanelContent>
      </ToolbarPanel>
    </Section>
  );
}

function HistoricalResultsSection(props: {
  isLoading: boolean;
  errorMessage: string | null;
  shouldShowNoRecords: boolean;
  displayedRows: HistoricalTableRow[];
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  tableState: ReturnType<typeof useDataTable<HistoricalTableRow>>;
}) {
  return (
    <React.Fragment>
      {props.isLoading ? <Text>Loading historical records...</Text> : null}

      {props.errorMessage ? (
        <Text color="$web.semantic.color.text.status.error">
          {props.errorMessage}
        </Text>
      ) : null}

      {props.shouldShowNoRecords ? (
        <Text color="$web.semantic.color.text.status.error">
          {NO_RECORDS_MESSAGE}
        </Text>
      ) : null}

      {props.displayedRows.length > 0 ? (
        <Section>
          <DataTableWrapper ref={props.tableContainerRef}>
            <DataTable
              title="Historical RFI records"
              hideHeader
              tableState={props.tableState}
            >
              <DataTable.SlotWrapper>
                <Heading level={3} size="md" textAlign="left">
                  Historical RFI records
                </Heading>
                <DataTable.Pagination variant="minimal" showPageSizeDropdown />
              </DataTable.SlotWrapper>
              <DataTable.Table />
            </DataTable>
          </DataTableWrapper>
        </Section>
      ) : null}
    </React.Fragment>
  );
}

export const HistoricalData = () => {
  const { session } = useAuth();
  const isInternalUser = session?.authType === 'INTERNAL';
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const yearOptions = React.useMemo(() => {
    return [YEAR_PROMPT_OPTION, ...getHistoricalYearOptions()];
  }, []);

  const [searchKey, setSearchKey] = React.useState('');
  const [quickFilterInput, setQuickFilterInput] = React.useState('');
  const [quickFilter, setQuickFilter] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState('');
  const [results, setResults] = React.useState<PolicySummary[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [isRfiClosedDialogOpen, setIsRfiClosedDialogOpen] = React.useState(false);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setQuickFilter(quickFilterInput);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [quickFilterInput]);

  React.useEffect(() => {
    if (!session?.token) {
      return;
    }

    void getAllQuestionType().catch((error) => {
      if (error instanceof AuthInterceptedError) {
        return;
      }
    });
  }, [session?.token]);

  React.useEffect(() => {
    if (!session?.token || isInternalUser) {
      return;
    }

    const loadExternalRows = async () => {
      const policyIds = readPolicyIdsFromSessionStorage();
      if (policyIds.length === 0) {
        setResults([]);
        setErrorMessage(null);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const data = await getHistoricalPoliciesByPolicyIds({
          policyIds,
          portal: session.externalContext?.portal,
        });
        setResults(data);
      } catch (error) {
        if (error instanceof AuthInterceptedError) {
          return;
        }
        setResults([]);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to load historical records.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    void loadExternalRows();
  }, [isInternalUser, session?.token]);

  const tableRows = React.useMemo(() => {
    return getPolicyTableRows(results, FALLBACK_VALUE, DEFAULT_STATUS);
  }, [results]);

  const yearFilteredRows = React.useMemo(() => {
    return selectedYear
      ? tableRows.filter((row) => {
          return row.rawSurveyYear === selectedYear;
        })
      : tableRows;
  }, [selectedYear, tableRows]);

  const filteredRows = React.useMemo(() => {
    return filterTableRows(
      yearFilteredRows,
      quickFilter,
      QUICK_FILTER_MIN_LENGTH
    );
  }, [quickFilter, yearFilteredRows]);

  const displayedRows = React.useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      return a.employerGroupName.localeCompare(b.employerGroupName);
    });
  }, [filteredRows]);

  const tableContainerWidth = useTableContainerWidth(tableContainerRef, displayedRows.length, 1200);

  const handleRowClick = React.useCallback(() => {
    // Historical rows are read-only; there is no Survey page to display here (matches legacy behavior).
    setIsRfiClosedDialogOpen(true);
  }, []);

  const handleDownload = React.useCallback(async (row: HistoricalTableRow) => {
    const surveyId = Number.parseInt(row.rawSurveyId, 10);
    const reportingYear = Number.parseInt(row.rawSurveyYear, 10);

    if (Number.isNaN(surveyId) || Number.isNaN(reportingYear)) {
      return;
    }

    try {
      await downloadSurveyExcel({
        customerNumber: row.rawCustomerNumber,
        customerDetails: {
          employerGroupEIN: row.rawEmployerGroupEIN,
          fundingType: row.fundingType,
          reportingYear,
          policyList: row.policyNumber,
        },
        surveyId,
        employerGroupName: row.employerGroupName,
        surveyYear: row.rawSurveyYear,
      });
    } catch (error) {
      if (error instanceof AuthInterceptedError) {
        return;
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to download historical RFI.'
      );
    }
  }, []);

  const columns = React.useMemo(() => {
    return getHistoricalColumns({
      containerWidth: tableContainerWidth,
      onRowClick: handleRowClick,
      onDownloadClick: handleDownload,
    });
  }, [handleDownload, handleRowClick, tableContainerWidth]);

  const tableState = useDataTable<HistoricalTableRow>({
    initialData: displayedRows,
    initialColumns: columns,
    rowIdKey: 'uniqueId',
    tableConfig: {
      ...TWO_STATE_SORTING_TABLE_CONFIG,
      enableColumnResizing: true,
      columnResizeMode: 'onEnd',
    },
    defaultSettingsConfig: {
      headerDisplaySettings: {
        hideSortingButton: false,
      },
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    },
  });

  React.useEffect(() => {
    tableState.setColumns(columns);
  }, [columns]);

  React.useEffect(() => {
    tableState.setData(displayedRows);
  }, [displayedRows]);

  const shouldShowNoRecords = computeShouldShowNoRecords({
    isInternalUser,
    isLoading,
    errorMessage,
    hasSearched,
    rowCount: displayedRows.length,
  });

  const runInternalSearch = React.useCallback(async () => {
    const normalizedSearch = searchKey.trim();
    setHasSearched(true);

    if (!normalizedSearch) {
      setResults([]);
      setErrorMessage('Enter a search term before searching.');
      return;
    }

    if (!session?.token) {
      setResults([]);
      setErrorMessage('No authenticated session found. Please sign in again.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const data = await searchHistoricalPoliciesBySearchId({
        searchId: normalizedSearch,
        surveyYear: selectedYear || undefined,
      });
      setResults(data);
    } catch (error) {
      if (error instanceof AuthInterceptedError) {
        return;
      }
      setResults([]);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to load historical records.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, selectedYear, session?.token]);

  const hasSearchInput = searchKey.trim().length > 0;
  const canRunSearch = hasSearchInput && !isLoading;
  const hasQuickFilterInput = quickFilterInput.trim().length > 0;
  const canClearQuickFilter = hasQuickFilterInput && !isLoading;

  return (
    <React.Fragment>
      <Router.MetaTags title="Prior Years RFI" />
      <Page>
        {isInternalUser ? (
          <InternalToolbar
            searchKey={searchKey}
            onSearchKeyChange={setSearchKey}
            onSearchKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void runInternalSearch();
              }
            }}
            onSearchClick={() => {
              void runInternalSearch();
            }}
            canRunSearch={canRunSearch}
            quickFilterInput={quickFilterInput}
            onQuickFilterChange={setQuickFilterInput}
            onQuickFilterClear={() => {
              setQuickFilterInput('');
            }}
            canClearQuickFilter={canClearQuickFilter}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            yearOptions={yearOptions}
            hasResults={displayedRows.length > 0}
            showRecordCount={
              quickFilter.trim().length >= QUICK_FILTER_MIN_LENGTH
            }
            recordCount={displayedRows.length}
          />
        ) : (
          <ExternalToolbar
            quickFilterInput={quickFilterInput}
            onQuickFilterChange={setQuickFilterInput}
            onQuickFilterClear={() => {
              setQuickFilterInput('');
            }}
            canClearQuickFilter={canClearQuickFilter}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            yearOptions={yearOptions}
            hasResults={displayedRows.length > 0}
            showRecordCount={
              quickFilter.trim().length >= QUICK_FILTER_MIN_LENGTH
            }
            recordCount={displayedRows.length}
          />
        )}

        <HistoricalResultsSection
          isLoading={isLoading}
          errorMessage={errorMessage}
          shouldShowNoRecords={shouldShowNoRecords}
          displayedRows={displayedRows}
          tableContainerRef={tableContainerRef}
          tableState={tableState}
        />

        <SharedDialog
          mode="app"
          ariaLabel="RFI Closed"
          isOpen={isRfiClosedDialogOpen}
          onClose={() => {
            setIsRfiClosedDialogOpen(false);
          }}
          title="RFI Closed"
          description="If the RFI Status is Complete, a copy of the submission can be downloaded by selecting the Download link in the 'Download RFI' column."
          primaryAction={{
            label: 'Close',
            onClick: () => {
              setIsRfiClosedDialogOpen(false);
            },
          }}
        />
      </Page>
    </React.Fragment>
  );
};
